// My Trading Journey: the trader's whole story. Timeline (month by month, with the moments that
// mattered), Then vs now (an earlier stretch of trading against the latest), Milestones (badges
// earned and the next ones), and every dollar in and out across prop firms, personal accounts,
// courses and tools. Pages: JourneyPages.js and BusinessPage.js (Money).
import { accountStatus, balanceOnDay } from './balance';
import { accountHistory, evaluationRecord, expandExpenses, firmOf, isProp, moneySummary, payoutsOf } from './business';
import { detectBehavior } from './behaviorDetect';
import { findEdge } from './edge';
import { rootSymbol } from './tools/contracts';
import { dayKey, netPnl, tradingDayKey } from './pnl';

const sum = (list, f) => list.reduce((s, x) => s + f(x), 0);
const isJournaled = (n) => Boolean(n && ((n.morningDone || []).length || (n.eveningDone || []).length || (n.review || '').trim() || (n.plan || '').trim()));
const addDays = (key, n) => {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// --- Real money vs prop accounts -------------------------------------------------------------

// Evaluations and funded accounts trade simulated money: what's real there is the fees and the
// payouts. Personal and live accounts trade real money.
export const isSimulated = (account) => Boolean(account) && (account.type === 'Evaluation' || account.type === 'Funded');

// A list of trades' result split into real money and prop (simulated), and whether each has trades.
export function realVsProp(trades, accounts) {
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const out = { real: 0, prop: 0, hasReal: false, hasProp: false };
  for (const t of trades) {
    if (isSimulated(byId.get(String(t.accountId)))) {
      out.prop += netPnl(t);
      out.hasProp = true;
    } else {
      out.real += netPnl(t);
      out.hasReal = true;
    }
  }
  return out;
}

// --- Prop firms, personal, or all together ---------------------------------------------------

// The Journey's switch: prop firms (evaluations, funded and live accounts: payouts minus what the
// firms were paid), personal accounts (the trader's own money: trading profit minus their costs),
// or everything together. Traders with only one kind of account see everything.
export const JOURNEY_SCOPES = ['prop', 'personal', 'all'];

// The Journey's one period selector, shared by every part: all time, the last 3 or 12 months
// (this month included), or a calendar year ('2025').
export const JOURNEY_PERIODS = ['all', 'last3', 'last12'];
export function periodRange(period, today = dayKey(new Date())) {
  if (!period || period === 'all') return {};
  if (/^\d{4}$/.test(period)) return { from: `${period}-01-01`, to: `${period}-12-31` };
  const back = period === 'last3' ? 2 : 11;
  const d = new Date(`${today.slice(0, 7)}-15T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - back);
  return { from: `${d.toISOString().slice(0, 7)}-01`, to: today };
}
const inPeriod = (month, range) => (!range.from || month >= range.from.slice(0, 7)) && (!range.to || month <= range.to.slice(0, 7));

// The three headline numbers at the top of the Journey, one per part: the real money result in
// the period (and the last 3 months), whether accounts are lasting longer (or, without prop
// accounts, whether the average month is better than the first ones), and the best setup with
// enough trades for a verdict. `o` is journeyOverview, `edge` is careerEdge.
export function journeyHeadlines(o, edge, period = 'all', today = dayKey(new Date())) {
  if (!o) return null;
  const range = periodRange(period, today);
  const last3 = periodRange('last3', today);
  const money = {
    net: sum(o.months.filter((m) => inPeriod(m.month, range)), (m) => m.cash),
    last3: sum(o.months.filter((m) => inPeriod(m.month, last3)), (m) => m.cash),
  };
  const blown = o.history.filter((h) => h.outcome === 'failed' || h.outcome === 'blown').sort((a, b) => a.start.localeCompare(b.start));
  const n = Math.min(3, Math.floor(blown.length / 2));
  const avg = (list) => sum(list, (h) => h.days) / list.length;
  const traded = o.months.filter((m) => m.traded);
  let better = null;
  if (n >= 2) better = { kind: 'lifespan', early: avg(blown.slice(0, n)), recent: avg(blown.slice(-n)) };
  else if (traded.length >= 6) better = { kind: 'month', early: sum(traded.slice(0, 3), (m) => m.pnl) / 3, recent: sum(traded.slice(-3), (m) => m.pnl) / 3 };
  const solid = edge ? edge.setups.filter((x) => x.verdict === 'edge' || x.verdict === 'keep').sort((a, b) => b.expectancy - a.expectancy) : [];
  return { money, better, setup: solid[0] || null };
}
export const hasBothKinds = (accounts) => accounts.some(isProp) && accounts.some((a) => !isProp(a));
const PROP_CATEGORIES = ['evaluation', 'reset', 'activation', 'subscription'];

// Which side an expense belongs to: its account's, else the side the trader picked, else prop
// when it's a firm fee (buying an account, a reset, activation) or paid to a prop firm. Every
// other trading cost (data, platforms, courses) is the trader's own: personal, when they have
// personal accounts, else general. General costs ("both", picked) count only all together.
export function expenseScope(e, byId) {
  if (e.accountId) {
    const a = byId.get(String(e.accountId));
    if (a) return isProp(a) ? 'prop' : 'personal';
  }
  if (['prop', 'personal', 'general'].includes(e.side)) return e.side;
  if (PROP_CATEGORIES.includes(e.category) || firmOf({ name: e.firm || '' })) return 'prop';
  return [...byId.values()].some((a) => !isProp(a)) ? 'personal' : 'general';
}

export function scopeJourney(scope, accounts, trades, expenses = []) {
  if (scope !== 'prop' && scope !== 'personal') return { accounts, trades, expenses };
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const shown = accounts.filter((a) => (scope === 'prop') === isProp(a));
  const ids = new Set(shown.map((a) => String(a._id)));
  return {
    accounts: shown,
    trades: trades.filter((t) => ids.has(String(t.accountId))),
    expenses: expenses.filter((e) => expenseScope(e, byId) === scope),
  };
}

// What each side adds up to, for the All together view: prop payouts minus prop costs, personal
// trading profit minus personal costs, and general costs.
export function scopeTotals(accounts, trades, expenses = [], today = dayKey(new Date())) {
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const spent = { prop: 0, personal: 0, general: 0 };
  for (const e of expandExpenses(expenses, today)) spent[expenseScope(e, byId)] += e.amount;
  const payouts = sum(payoutsOf(accounts), (p) => p.amount);
  const personalPnl = sum(trades.filter((t) => { const a = byId.get(String(t.accountId)); return a && !isProp(a); }), netPnl);
  return {
    prop: payouts - spent.prop, personal: personalPnl - spent.personal, general: spent.general,
    payouts, personalPnl, net: payouts + personalPnl - spent.prop - spent.personal - spent.general,
  };
}

// --- Am I profitable?: the money engine -------------------------------------------------------

// Every real-money entry, oldest first: each expense payment (monthly ones once a month), each
// payout, each month of trading in a personal account (prop accounts trade simulated money, so
// their P&L is never here; Live prop accounts count through their payouts), and the trader's own
// money moving in and out of personal accounts (`transfer`: shown, never counted in the result).
// Every number on Am I profitable? is a sum of these rows, so the page can't disagree with itself.
// `amount` is signed from the trader's pocket: costs negative, payouts and profit positive.
export function moneyLedger(accounts, trades, expenses = [], today = dayKey(new Date())) {
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const rows = [];
  for (const e of expandExpenses(expenses, today)) {
    const a = byId.get(String(e.accountId));
    rows.push({ id: `e:${e._id}:${e.date}`, date: e.date, kind: 'expense', category: e.category, firm: e.firm || firmOf(a) || '',
      accountId: e.accountId || null, account: a ? a.name : '', amount: -e.amount, note: e.note || '', expense: e.source || e });
  }
  for (const p of payoutsOf(accounts)) {
    const a = byId.get(String(p.accountId));
    rows.push({ id: `p:${p.accountId}:${p.date}:${p.amount}`, date: p.date, kind: 'payout', category: '', firm: p.firm || '', accountId: p.accountId, account: a ? a.name : '', amount: p.amount, note: p.note || '' });
  }
  const months = {};
  for (const t of trades) {
    const a = byId.get(String(t.accountId));
    if (!a || isProp(a)) continue;
    const day = tradingDayKey(t.date);
    const key = `${t.accountId}:${day.slice(0, 7)}`;
    const m = (months[key] = months[key] || { accountId: t.accountId, account: a.name, month: day.slice(0, 7), last: day, amount: 0, trades: 0 });
    m.amount += netPnl(t);
    m.trades += 1;
    if (day > m.last) m.last = day;
  }
  for (const m of Object.values(months)) {
    rows.push({ id: `t:${m.accountId}:${m.month}`, date: m.last, kind: 'trading', category: '', firm: '', accountId: m.accountId, account: m.account, amount: m.amount, trades: m.trades, note: '' });
  }
  for (const a of accounts) {
    if (isProp(a) || a.attemptOf) continue;
    const started = a.createdAt ? dayKey(a.createdAt) : null;
    if (a.size && started) rows.push({ id: `s:${a._id}`, date: started, kind: 'deposit', transfer: true, firm: '', accountId: a._id, account: a.name, amount: -a.size, note: 'Starting balance' });
    for (const f of a.cashFlows || []) {
      if (f.kind !== 'deposit' && f.kind !== 'withdrawal') continue;
      rows.push({ id: `f:${a._id}:${f.date}:${f.kind}:${f.amount}`, date: f.date, kind: f.kind, transfer: true, firm: '', accountId: a._id, account: a.name, amount: f.kind === 'deposit' ? -f.amount : f.amount, note: f.note || '' });
    }
  }
  return rows.filter((r) => r.date <= today).sort((x, y) => x.date.localeCompare(y.date) || x.id.localeCompare(y.id));
}

// The statement's columns on each side of the switch. Firm fees are split out on prop, folded
// into one on All together; data and platforms are "tools".
export const STATEMENT_COLUMNS = {
  prop: ['payouts', 'evaluation', 'reset', 'activation', 'subscription', 'other'],
  personal: ['trading', 'tools', 'education', 'other'],
  all: ['payouts', 'trading', 'firmFees', 'tools', 'education', 'other'],
};
export const COLUMN_NAMES = {
  payouts: 'Payouts', trading: 'Personal trading', evaluation: 'Evaluations', reset: 'Resets', activation: 'Activation',
  subscription: 'Monthly fees', firmFees: 'Prop firm fees', tools: 'Data & platforms', education: 'Courses & signals', other: 'Other',
};
const FIRM_FEES = ['evaluation', 'reset', 'activation', 'subscription'];
export function columnOf(row, scope) {
  if (row.transfer) return null;
  if (row.kind === 'payout') return 'payouts';
  if (row.kind === 'trading') return 'trading';
  const c = row.category;
  if (scope === 'prop') return FIRM_FEES.includes(c) ? c : 'other';
  if (FIRM_FEES.includes(c)) return scope === 'all' ? 'firmFees' : 'other';
  if (c === 'data' || c === 'platform') return 'tools';
  return c === 'education' ? 'education' : 'other';
}

const periodKey = (date, unit) => (unit === 'year' ? date.slice(0, 4)
  : unit === 'quarter' ? `${date.slice(0, 4)}-Q${Math.floor((Number(date.slice(5, 7)) - 1) / 3) + 1}` : date.slice(0, 7));

// The money statement: one row per month (quarter, year) from the first entry to today, each
// column's total, the net and the running total since the start, plus a totals row for the
// rows shown (`range` from periodRange). Months with nothing in them are listed too.
export function moneyStatement(ledger, scope, unit = 'month', range = {}, today = dayKey(new Date())) {
  const counted = ledger.filter((r) => !r.transfer);
  if (!counted.length) return { rows: [], totals: { cells: {}, net: 0 }, columns: STATEMENT_COLUMNS[scope] || STATEMENT_COLUMNS.all };
  const groups = new Map();
  for (let m = counted[0].date.slice(0, 7); m <= today.slice(0, 7) && groups.size < 400; m = nextMonth(m)) {
    const key = periodKey(`${m}-01`, unit);
    if (!groups.has(key)) groups.set(key, { key, from: `${m}-01`, cells: {}, net: 0 });
  }
  for (const r of counted) {
    const g = groups.get(periodKey(r.date, unit));
    if (!g) continue;
    const col = columnOf(r, scope);
    g.cells[col] = (g.cells[col] || 0) + r.amount;
    g.net += r.amount;
  }
  let running = 0;
  const all = [...groups.values()].map((g) => {
    running += g.net;
    return { ...g, running };
  });
  const rows = all.filter((g) => {
    const last = unit === 'year' ? `${g.key}-12` : unit === 'quarter' ? `${g.key.slice(0, 4)}-${String(Number(g.key.slice(-1)) * 3).padStart(2, '0')}` : g.key;
    return (!range.from || last >= range.from.slice(0, 7)) && (!range.to || g.from.slice(0, 7) <= range.to.slice(0, 7));
  });
  const totals = { cells: {}, net: 0 };
  for (const g of rows) {
    for (const [c, v] of Object.entries(g.cells)) totals.cells[c] = (totals.cells[c] || 0) + v;
    totals.net += g.net;
  }
  return { rows, totals, columns: STATEMENT_COLUMNS[scope] || STATEMENT_COLUMNS.all };
}

// --- Money: every dollar in and out --------------------------------------------------------

// Personal accounts: the starting balance counts as money put in on the day the account began.
// `held` is how much more the accounts held at the end of the range than at its start (an
// account started inside the range held nothing before), so a period's result counts money
// still sitting in them.
export function personalMoney(accounts, trades, range = {}, today = dayKey(new Date())) {
  const inRange = (d) => (!range.from || d >= range.from) && (!range.to || d <= range.to);
  const byAccount = {};
  for (const t of trades) (byAccount[String(t.accountId)] = byAccount[String(t.accountId)] || []).push(t);
  const rows = accounts.filter((a) => a.type === 'Personal' && !a.attemptOf).map((a) => {
    const list = byAccount[String(a._id)] || [];
    const first = list.map((t) => dayKey(t.date)).sort()[0];
    const started = a.createdAt ? dayKey(a.createdAt) : first || dayKey(new Date());
    const flows = (a.cashFlows || []).filter((f) => inRange(f.date));
    const deposits = sum(flows.filter((f) => f.kind === 'deposit'), (f) => f.amount) + (a.size && inRange(started) ? a.size : 0);
    const withdrawals = sum(flows.filter((f) => f.kind === 'withdrawal'), (f) => f.amount);
    const pnl = sum(list.filter((t) => inRange(dayKey(t.date))), netPnl);
    const status = a.size ? accountStatus(a, list) : null;
    const end = range.to && range.to < today ? range.to : today;
    const atEnd = started <= end ? balanceOnDay(a, list, end) : 0;
    const atStart = range.from && started < range.from ? balanceOnDay(a, list, addDays(range.from, -1)) : 0;
    return { account: a, deposits, withdrawals, pnl, balance: status ? status.balance : null, held: (atEnd || 0) - (atStart || 0) };
  });
  return {
    rows, held: sum(rows, (r) => r.held),
    deposits: sum(rows, (r) => r.deposits), withdrawals: sum(rows, (r) => r.withdrawals), pnl: sum(rows, (r) => r.pnl),
    balance: rows.some((r) => r.balance !== null) ? sum(rows, (r) => r.balance || 0) : null,
  };
}

// --- Going live ---------------------------------------------------------------------------

// The days the trader reached a live account, oldest first: each live account starting, and a
// funded account that ended by moving to live when no live account started near that day (the
// same move logged both ways counts once).
export function liveDays(history) {
  const starts = history.filter((h) => h.type === 'Live').map((h) => ({ date: h.start, name: h.account.name }));
  const near = (a, b) => Math.abs(Date.parse(a) - Date.parse(b)) <= 14 * 864e5;
  const moves = history.filter((h) => h.end && h.account.endCause === 'went_live' && !starts.some((s) => near(s.date, h.end)))
    .map((h) => ({ date: h.end, name: h.account.name }));
  return [...starts, ...moves].sort((a, b) => a.date.localeCompare(b.date));
}

// --- Timeline -------------------------------------------------------------------------------

// Month by month: P&L, trades, win rate and days journaled, with the moments that mattered.
// Events: { date, kind, ...details } — the page writes the words.
export function timeline(accounts, trades, notes = {}, expenses = [], goals = [], moments = []) {
  const events = [];
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  if (sorted.length) events.push({ date: dayKey(sorted[0].date), kind: 'firstTrade', symbol: sorted[0].symbol });
  const journaled = Object.entries(notes).filter(([, n]) => isJournaled(n)).map(([k, n]) => n.date || k).sort();
  if (journaled.length) events.push({ date: journaled[0], kind: 'firstJournal' });

  const history = accountHistory(accounts, trades, expenses);
  for (const d of liveDays(history)) events.push({ date: d.date, kind: 'wentLive', name: d.name });
  for (const h of history) {
    if (h.type !== 'Live') events.push({ date: h.start, kind: 'accountStart', name: h.account.name, type: h.type });
    if (h.end && ['passed', 'failed', 'blown'].includes(h.outcome)) {
      events.push({ date: h.end, kind: h.outcome === 'passed' ? 'passed' : 'lost', name: h.account.name, days: h.days, accountId: h.account._id,
        cause: h.account.endCause, lesson: h.account.lesson, note: h.account.attemptNote });
    }
  }
  for (const a of accounts.filter((x) => x.type === 'Personal')) events.push({ date: a.createdAt ? dayKey(a.createdAt) : (sorted[0] ? dayKey(sorted[0].date) : dayKey(new Date())), kind: 'accountStart', name: a.name, type: 'Personal' });
  payoutsOf(accounts).forEach((p, i) => events.push({ date: p.date, kind: 'payout', amount: p.amount, first: i === 0, firm: p.firm }));

  // New best and worst trading days, once there's a record to beat.
  const byDay = {};
  for (const t of sorted) byDay[tradingDayKey(t.date)] = (byDay[tradingDayKey(t.date)] || 0) + netPnl(t);
  let best = 0;
  let worst = 0;
  Object.keys(byDay).sort().forEach((d, i) => {
    const v = byDay[d];
    if (v > best) { if (i >= 5 && v > 0) events.push({ date: d, kind: 'bestDay', amount: v }); best = v; }
    if (v < worst) { if (i >= 5 && v < 0) events.push({ date: d, kind: 'worstDay', amount: v }); worst = v; }
  });
  for (const m of milestones(accounts, trades, notes)) if (m.earnedOn && m.timeline) events.push({ date: m.earnedOn, kind: 'milestone', id: m.id });
  for (const g of goals) {
    if (g.createdAt) events.push({ date: dayKey(g.createdAt), kind: 'goalSet', title: g.title });
    if (g.doneAt) events.push({ date: g.doneAt, kind: 'goalReached', title: g.title });
  }

  for (const m of moments) events.push({ date: m.date, kind: 'moment', id: m.id, title: m.title, text: m.text });

  const months = {};
  const month = (key) => (months[key] = months[key] || { month: key, pnl: 0, real: 0, prop: 0, hasReal: false, hasProp: false, trades: 0, wins: 0, days: new Set(), journaled: 0, events: [] });
  const simulated = new Set(accounts.filter(isSimulated).map((a) => String(a._id)));
  for (const t of sorted) {
    const m = month(tradingDayKey(t.date).slice(0, 7));
    m.pnl += netPnl(t);
    if (simulated.has(String(t.accountId))) {
      m.prop += netPnl(t);
      m.hasProp = true;
    } else {
      m.real += netPnl(t);
      m.hasReal = true;
    }
    m.trades += 1;
    if (netPnl(t) > 0) m.wins += 1;
    m.days.add(tradingDayKey(t.date));
  }
  for (const d of journaled) month(d.slice(0, 7)).journaled += 1;
  for (const e of events) month(e.date.slice(0, 7)).events.push(e);
  return Object.values(months)
    .map((m) => ({ ...m, tradingDays: m.days.size, winRate: m.trades ? (m.wins / m.trades) * 100 : null, events: m.events.sort((a, b) => a.date.localeCompare(b.date)) }))
    .sort((a, b) => b.month.localeCompare(a.month));
}

// The key a reflection on an event is saved under (accounts that ended keep theirs on the account).
export const eventKey = (e) => `${e.kind}:${e.date}:${String(e.name || e.title || e.amount || e.id || e.symbol || '').slice(0, 80)}`;

// --- Story: chapters ------------------------------------------------------------------------

// The journey in chapters, from the months of journeyOverview (oldest first): the first three
// months, the grind until the first green month (real money in beats money out), things clicking, and the green run the trader
// is on now (two green months or more in a row). Each with its result, money and accounts.
export const CHAPTER_NAMES = { start: 'The early days', grind: 'The grind', clicking: 'Things start clicking', green: 'In the green' };
export function chapters(overview) {
  if (!overview || !overview.months.length) return [];
  const months = overview.months;
  const n = months.length;
  const parts = [];
  if (n <= 3) parts.push(['start', 0, n - 1]);
  else {
    parts.push(['start', 0, 2]);
    const greenAt = months.findIndex((m, i) => i >= 3 && m.moved && m.cash > 0);
    let runAt = -1;
    let run = 0;
    for (let i = n - 1; i >= 3; i -= 1) {
      if (!months[i].moved) continue;
      if (months[i].cash <= 0) break;
      run += 1;
      runAt = i;
    }
    const greenRun = run >= 2 ? runAt : -1;
    if (greenAt === -1) parts.push(['grind', 3, n - 1]);
    else {
      if (greenAt > 3) parts.push(['grind', 3, greenAt - 1]);
      if (greenRun !== -1 && greenRun > greenAt) {
        parts.push(['clicking', greenAt, greenRun - 1]);
        parts.push(['green', greenRun, n - 1]);
      } else parts.push([greenRun === greenAt ? 'green' : 'clicking', greenAt, n - 1]);
    }
  }
  const history = overview.history || [];
  return parts.map(([id, a, b], i) => {
    const list = months.slice(a, b + 1);
    const from = list[0].month;
    const to = list[list.length - 1].month;
    const inside = (d) => d && d.slice(0, 7) >= from && d.slice(0, 7) <= to;
    const ended = history.filter((h) => inside(h.end));
    return {
      id, number: i + 1, from, to, months: list.map((m) => m.month),
      pnl: sum(list, (m) => m.pnl), real: sum(list, (m) => m.real), prop: sum(list, (m) => m.prop),
      hasReal: list.some((m) => m.hasReal), hasProp: list.some((m) => m.hasProp),
      spent: sum(list, (m) => m.spent), paid: sum(list, (m) => m.paid), own: sum(list, (m) => m.own), cash: sum(list, (m) => m.cash),
      greenMonths: list.filter((m) => m.moved && m.cash > 0).length, tradedMonths: list.filter((m) => m.moved).length,
      started: history.filter((h) => inside(h.start)).length,
      blown: ended.filter((h) => h.outcome === 'failed' || h.outcome === 'blown').length,
      passed: ended.filter((h) => h.outcome === 'passed').length,
      payouts: (overview.money.payouts || []).filter((p) => inside(p.date)).length,
    };
  });
}

// --- Story: lessons from blown accounts -----------------------------------------------------

// Every prop account that was blown, grouped by why (most first), with what happened and the
// lesson the trader wrote down, and the ones still missing a reason.
export function blownLessons(history) {
  const blown = history.filter((h) => h.outcome === 'failed' || h.outcome === 'blown').sort((a, b) => (b.end || '').localeCompare(a.end || ''));
  const groups = {};
  for (const h of blown) {
    const c = h.account.endCause || 'unknown';
    (groups[c] = groups[c] || { cause: c, accounts: [] }).accounts.push(h);
  }
  // What the trader did (behaviorCause), with what that mistake cost in those accounts (endSnapshot).
  const behaviors = {};
  for (const h of blown) {
    const k = h.account.behaviorCause;
    if (!k) continue;
    const g = (behaviors[k] = behaviors[k] || { cause: k, accounts: [], cost: 0 });
    g.accounts.push(h);
    const m = ((h.account.endSnapshot && h.account.endSnapshot.mistakes) || []).find((x) => x.kind === k);
    if (m) g.cost += m.cost;
  }
  // How long each blown account lasted, oldest first, and the first few against the latest few.
  const lasting = blown.filter((h) => h.days).slice().sort((a, b) => (a.start || '').localeCompare(b.start || ''));
  const k = Math.min(3, Math.floor(lasting.length / 2));
  const avgDays = (list) => (list.length ? Math.round(list.reduce((s2, h) => s2 + h.days, 0) / list.length) : null);
  return {
    count: blown.length,
    causes: Object.values(groups).filter((g) => g.cause !== 'unknown').sort((a, b) => b.accounts.length - a.accounts.length),
    unknown: groups.unknown ? groups.unknown.accounts : [],
    lessons: blown.filter((h) => h.account.lesson || h.account.attemptNote),
    behaviors: Object.values(behaviors).sort((a, b) => b.accounts.length - a.accounts.length || a.cost - b.cost),
    lasting, lastedThen: k ? avgDays(lasting.slice(0, k)) : null, lastedNow: k ? avgDays(lasting.slice(-k)) : null, compared: k,
  };
}

// --- Story: what changed --------------------------------------------------------------------

// The first months against the latest: spending per month (three months each side) and how long
// blown accounts lasted (the first few against the latest few). Trading numbers come from
// compareStretches.
export function whatChanged(overview) {
  if (!overview || overview.months.length < 6) return null;
  const avg = (list, f) => (list.length ? sum(list, f) / list.length : 0);
  const months = overview.months;
  const blown = overview.history.filter((h) => h.end && (h.outcome === 'failed' || h.outcome === 'blown')).sort((a, b) => a.start.localeCompare(b.start));
  const k = Math.min(3, Math.floor(blown.length / 2));
  return {
    spentThen: avg(months.slice(0, 3), (m) => m.spent), spentNow: avg(months.slice(-3), (m) => m.spent),
    pnlThen: avg(months.slice(0, 3), (m) => m.pnl), pnlNow: avg(months.slice(-3), (m) => m.pnl),
    livedThen: k ? avg(blown.slice(0, k), (h) => h.days) : null, livedNow: k ? avg(blown.slice(-k), (h) => h.days) : null,
  };
}

// --- Then vs now ----------------------------------------------------------------------------

// The numbers for one stretch of trading: results, risk, behavior and routine.
export function stretchStats(trades, notes, accounts, from, to) {
  const list = trades.filter((t) => { const d = tradingDayKey(t.date); return d >= from && d <= to; });
  const wins = list.filter((t) => netPnl(t) > 0);
  const losses = list.filter((t) => netPnl(t) < 0);
  const days = new Set(list.map((t) => tradingDayKey(t.date)));
  const behavior = list.length ? detectBehavior(list, { accounts, notes }) : null;
  const costly = behavior ? behavior.flagged.count : 0;
  const revenge = behavior ? behavior.patterns.find((p) => p.kind === 'revenge').count : 0;
  const journaled = [...days].filter((d) => isJournaled(notes[d])).length;
  const lost = accountHistory(accounts, trades).filter((h) => (h.outcome === 'failed' || h.outcome === 'blown') && h.end && h.end >= from && h.end <= to);
  const grossWin = sum(wins, netPnl);
  const grossLoss = -sum(losses, netPnl);
  return {
    from, to, trades: list.length, tradingDays: days.size,
    net: sum(list, netPnl), perDay: days.size ? sum(list, netPnl) / days.size : 0,
    winRate: list.length ? (wins.length / list.length) * 100 : null,
    avgWin: wins.length ? grossWin / wins.length : null, avgLoss: losses.length ? -grossLoss / losses.length : null,
    profitFactor: grossLoss ? grossWin / grossLoss : null,
    worstDay: days.size ? Math.min(...[...days].map((d) => sum(list.filter((t) => tradingDayKey(t.date) === d), netPnl))) : null,
    tradesPerDay: days.size ? list.length / days.size : null,
    tiltShare: list.length ? (costly / list.length) * 100 : null,
    revengePerWeek: days.size ? (revenge / days.size) * 5 : null,
    journaledShare: days.size ? (journaled / days.size) * 100 : null,
    accountLife: lost.length ? sum(lost, (h) => h.days) / lost.length : null,
  };
}

// The two stretches to compare: 'start' = your first 30 days of trading vs your last 30;
// '3m' / '6m' = the 30 days that ended 3 or 6 months ago vs your last 30.
export function compareStretches(trades, notes, accounts, mode = 'start', today = dayKey(new Date())) {
  const days = trades.map((t) => tradingDayKey(t.date)).sort();
  if (!days.length) return null;
  const last = days[days.length - 1] < today ? days[days.length - 1] : today;
  const nowFrom = addDays(last, -29);
  let thenFrom;
  let thenTo;
  if (mode === 'start') {
    thenFrom = days[0];
    thenTo = addDays(days[0], 29);
  } else {
    thenTo = addDays(last, mode === '6m' ? -182 : -91);
    thenFrom = addDays(thenTo, -29);
  }
  if (thenTo >= nowFrom) return { tooSoon: true, firstDay: days[0] };
  const then = stretchStats(trades, notes, accounts, thenFrom, thenTo);
  const now = stretchStats(trades, notes, accounts, nowFrom, last);
  if (then.trades < 5 || now.trades < 5) return { tooFew: true, then, now };
  return { then, now };
}

// Each line of Then vs now: whether higher is better, so the page can mark it.
export const COMPARE_ROWS = [
  ['net', 'Net P&L', 'money', true], ['perDay', 'P&L per trading day', 'money', true], ['winRate', 'Win rate', 'pct', true],
  ['avgWin', 'Average win', 'money', true], ['avgLoss', 'Average loss', 'money', true], ['profitFactor', 'Profit factor', 'ratio', true],
  ['worstDay', 'Worst day', 'money', true], ['tradesPerDay', 'Trades per day', 'num', null], ['tiltShare', 'Trades flagged in Behavior', 'pct', false],
  ['revengePerWeek', 'Revenge trades per week', 'num', false], ['journaledShare', 'Trading days journaled', 'pct', true], ['accountLife', 'Days a lost account lasted', 'days', true],
];

// --- Am I getting better?: then vs now with any two periods, and trends -----------------------

// The periods Then and Now can be: the first or last 1, 3, 6 or 12 months of trading, or a year.
export const STRETCH_CHOICES = ['first30', 'first90', 'first180', 'last30', 'last90', 'last180', 'last365'];
export function stretchRange(choice, firstDay, today = dayKey(new Date())) {
  if (/^\d{4}$/.test(choice)) return { from: `${choice}-01-01`, to: `${choice}-12-31` < today ? `${choice}-12-31` : today };
  const n = Number(choice.replace(/\D/g, ''));
  return choice.startsWith('first') ? { from: firstDay, to: addDays(firstDay, n - 1) } : { from: addDays(today, -(n - 1)), to: today };
}

// Fewer than this and a measure says "not enough yet" instead of better or worse.
export const ENOUGH_TRADES = 20;
export const ENOUGH_ACCOUNTS = 3;

// One stretch measured for Am I getting better?: the trading measures (prop accounts included,
// as skill: simulated money), plus prop accounts that ended in it (blown, evaluations passed),
// firm fees per month (real money), and how much there was to judge from.
export function skillStats(trades, notes, accounts, expenses, from, to, today = dayKey(new Date())) {
  const base = stretchStats(trades, notes, accounts, from, to);
  const history = accountHistory(accounts, trades, expenses, today);
  const ended = history.filter((h) => h.end && h.end >= from && h.end <= to);
  const blown = ended.filter((h) => h.outcome === 'failed' || h.outcome === 'blown');
  const evals = evaluationRecord(ended);
  const end = to < today ? to : today;
  const months = Math.max(1, monthsBetween(from.slice(0, 7), end.slice(0, 7)) + 1);
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const fees = sum(expandExpenses(expenses, today).filter((e) => e.date >= from && e.date <= end && expenseScope(e, byId) === 'prop'), (e) => e.amount);
  return {
    ...base, blownPerMonth: history.length ? blown.length / months : null, passRate: evals.passRate, finished: evals.passed + evals.failed,
    feesPerMonth: history.length ? fees / months : null, ended: ended.length, blown: blown.length,
  };
}

// The rows of the comparison: label, format, whether higher is better (null: neither), and what
// the sample is ('trades' or 'accounts') so thin ones get no verdict.
export const SKILL_ROWS = [
  ['winRate', 'Win rate', 'pct', true, 'trades'], ['profitFactor', 'Profit factor', 'ratio', true, 'trades'],
  ['avgWin', 'Average win', 'money', true, 'trades'], ['avgLoss', 'Average loss', 'money', true, 'trades'],
  ['perDay', 'P&L per trading day', 'money', true, 'trades'], ['worstDay', 'Worst day', 'money', true, 'trades'],
  ['tradesPerDay', 'Trades per day', 'num', null, 'trades'], ['tiltShare', 'Trades flagged in Behavior', 'pct', false, 'trades'],
  ['revengePerWeek', 'Revenge trades per week', 'num', false, 'trades'], ['journaledShare', 'Trading days journaled', 'pct', true, 'trades'],
  ['accountLife', 'Days a blown account lasted', 'days', true, 'blown'], ['blownPerMonth', 'Accounts blown per month', 'num', false, 'months'],
  ['passRate', 'Evaluation pass rate', 'pct', true, 'finished'], ['feesPerMonth', 'Firm fees per month (real money)', 'cost', false, 'months'],
];
export function compareSkill(then, now) {
  const enough = (s, kind) => (kind === 'trades' ? s.trades >= ENOUGH_TRADES : kind === 'blown' ? s.blown >= ENOUGH_ACCOUNTS
    : kind === 'finished' ? s.finished >= ENOUGH_ACCOUNTS : true);
  const rows = SKILL_ROWS.map(([key, label, kind, higher, sample]) => {
    const a = then[key];
    const b = now[key];
    const thin = !enough(then, sample) || !enough(now, sample);
    const verdict = a === null || b === null || a === undefined || b === undefined ? null : thin ? 'thin'
      : higher === null ? null : Math.abs(b - a) < 1e-9 ? 'same' : (b > a) === higher ? 'better' : 'worse';
    return { key, label, kind, a, b, verdict };
  });
  return { rows, better: rows.filter((r) => r.verdict === 'better').length, worse: rows.filter((r) => r.verdict === 'worse').length, judged: rows.filter((r) => r.verdict === 'better' || r.verdict === 'worse' || r.verdict === 'same').length };
}

// The same measures period by period (months, quarters or years) for the trend charts.
export function skillTrend(trades, notes, accounts, expenses, unit = 'month', today = dayKey(new Date())) {
  const days = [...trades.map((t) => tradingDayKey(t.date)), ...accountHistory(accounts, trades, expenses, today).map((h) => h.start)].sort();
  if (!days.length) return [];
  const out = [];
  for (let m = days[0].slice(0, 7); m <= today.slice(0, 7) && out.length < 400;) {
    const y = Number(m.slice(0, 4));
    const mo = Number(m.slice(5, 7));
    const startMo = unit === 'year' ? 1 : unit === 'quarter' ? Math.floor((mo - 1) / 3) * 3 + 1 : mo;
    const span = unit === 'year' ? 12 : unit === 'quarter' ? 3 : 1;
    const from = `${y}-${String(startMo).padStart(2, '0')}-01`;
    const lastMo = startMo + span - 1;
    const to = addDays(`${lastMo === 12 ? y + 1 : y}-${String(lastMo === 12 ? 1 : lastMo + 1).padStart(2, '0')}-01`, -1);
    const key = unit === 'year' ? String(y) : unit === 'quarter' ? `${y}-Q${(startMo + 2) / 3}` : from.slice(0, 7);
    out.push({ key, from, ...skillStats(trades, notes, accounts, expenses, from, to < today ? to : today, today) });
    m = addDays(to, 1).slice(0, 7);
  }
  return out;
}

// --- Turning points: what changed, and what happened after -------------------------------------

// Days before and after a change that are compared.
export const TURNING_WINDOW = 60;
// The measures a turning point looks at, whether higher is better, and the smallest relative
// change that counts (account life and pass rate move in bigger steps).
// Journaling isn't one: it jumps when a trader starts using Market Matrix, not when they change.
const TURNING_MEASURES = [
  ['accountLife', true, 0.25], ['revengePerWeek', false, 0.25], ['tiltShare', false, 0.2], ['winRate', true, 0.1],
  ['avgLoss', true, 0.15], ['perDay', true, 0.25],
];
// A change needs this many days of trading after it before it's judged.
export const TURNING_MIN_AFTER = 21;

// The dated changes a trader made, from what Market Matrix already records: the first day a new
// rule was ticked, Playbook plays added or retired, experiments, moments they logged, lessons
// written on blown accounts, courses or signals they stopped paying for, and their average size
// on one instrument dropping by a third or more. Changes in the first two weeks of trading, or
// of using Market Matrix (the first account added: setting up rules and plays that week is where
// they started, not a change), don't count. Each: { date, kind, title }.
export function journeyChanges({ trades = [], notes = {}, accounts = [], expenses = [], plays = [], experiments = [], moments = [] }, today = dayKey(new Date())) {
  const days = trades.map((t) => tradingDayKey(t.date)).sort();
  if (!days.length) return [];
  const joined = accounts.map((a) => a.createdAt && dayKey(a.createdAt)).filter(Boolean).sort()[0];
  const setupEnd = joined ? addDays(joined, 14) : null;
  const settled = addDays(days[0], 14);
  const out = [];
  const firstRule = {};
  for (const [key, n] of Object.entries(notes)) {
    const d = (n && n.date) || key;
    for (const r of (n && n.rulesFollowed) || []) if (!firstRule[r] || d < firstRule[r]) firstRule[r] = d;
  }
  for (const [rule, d] of Object.entries(firstRule)) out.push({ date: d, kind: 'rule', title: rule });
  for (const p of plays) {
    if (p.createdAt) out.push({ date: dayKey(p.createdAt), kind: 'play', title: p.name });
    if (p.status === 'retired' && p.updatedAt) out.push({ date: dayKey(p.updatedAt), kind: 'retired', title: p.name });
  }
  for (const x of experiments) if (x.startedOn) out.push({ date: x.startedOn, kind: 'experiment', title: x.title });
  for (const m of moments) if (m.date) out.push({ date: m.date, kind: 'moment', title: m.title || m.text || '' });
  const lessonOn = {};
  for (const h of accountHistory(accounts, trades, [], today)) {
    const text = (h.account.lesson || '').trim();
    if (text && h.end && (!lessonOn[text] || h.end < lessonOn[text])) lessonOn[text] = h.end;
  }
  for (const [text, d] of Object.entries(lessonOn)) out.push({ date: d, kind: 'lesson', title: text });
  for (const e of expenses) {
    if (e.repeat === 'monthly' && e.until && e.until < today && (e.category === 'education' || e.category === 'data' || e.category === 'platform')) {
      out.push({ date: e.until, kind: 'stopped', title: e.note || e.firm || '' });
    }
  }
  // Average size by month on each instrument (sizes on different ones can't be compared): a month
  // a third or more below the three before it, that the next month didn't undo.
  const byRoot = {};
  for (const t of trades) {
    const root = rootSymbol(t.symbol || '') || t.symbol || '';
    const m = tradingDayKey(t.date).slice(0, 7);
    const x = ((byRoot[root] = byRoot[root] || {})[m] = (byRoot[root] || {})[m] || { n: 0, size: 0 });
    x.n += 1;
    x.size += Math.abs(Number(t.size) || 0);
  }
  for (const [root, sizeBy] of Object.entries(byRoot)) {
    const months = Object.keys(sizeBy).sort().filter((m) => sizeBy[m].n >= 5);
    const avg = (m) => sizeBy[m].size / sizeBy[m].n;
    for (let i = 3; i < months.length; i += 1) {
      const before = (avg(months[i - 1]) + avg(months[i - 2]) + avg(months[i - 3])) / 3;
      const now = avg(months[i]);
      const next = months[i + 1] ? avg(months[i + 1]) : now;
      if (before > 0 && now <= before * 0.67 && next <= before * 0.8) {
        out.push({ date: `${months[i]}-01`, kind: 'size', title: root, from: before, to: now });
        i += 2;
      }
    }
  }
  // Rules, plays and experiments made while setting up are the starting point. Moments, lessons
  // and size changes carry the day they happened, so they count whenever that was.
  const setUp = (c) => ['rule', 'play', 'retired', 'experiment'].includes(c.kind) && setupEnd && c.date < setupEnd;
  return out.filter((c) => c.date >= settled && c.date <= today && !setUp(c)).sort((a, b) => a.date.localeCompare(b.date));
}

// Each change (or changes within a week of each other) measured: the TURNING_WINDOW days before
// against the days after. A turning point is one where at least two measures moved clearly the
// same way and fewer moved the other way, with enough trades on both sides. 'helped' or 'hurt',
// strongest first.
export function turningPoints(input, today = dayKey(new Date())) {
  const { trades = [], notes = {}, accounts = [], expenses = [] } = input;
  const changes = journeyChanges(input, today);
  const groups = [];
  for (const c of changes) {
    const g = groups[groups.length - 1];
    if (g && c.date <= addDays(g.date, 7)) g.changes.push(c);
    else groups.push({ date: c.date, changes: [c] });
  }
  const points = [];
  for (const g of groups) {
    const beforeFrom = addDays(g.date, -TURNING_WINDOW);
    const afterTo = addDays(g.date, TURNING_WINDOW) < today ? addDays(g.date, TURNING_WINDOW) : today;
    if (addDays(g.date, TURNING_MIN_AFTER) > today) continue;
    const before = skillStats(trades, notes, accounts, expenses, beforeFrom, addDays(g.date, -1), today);
    const after = skillStats(trades, notes, accounts, expenses, g.date, afterTo, today);
    if (before.trades < ENOUGH_TRADES || after.trades < ENOUGH_TRADES) continue;
    const moves = [];
    for (const [key, higher, step] of TURNING_MEASURES) {
      const a = before[key];
      const b = after[key];
      if (a === null || b === null || a === undefined || b === undefined) continue;
      if (key === 'accountLife' && (before.blown < 2 || after.blown < 2)) continue;
      const base = Math.max(Math.abs(a), key === 'winRate' || key === 'tiltShare' || key === 'journaledShare' ? 10 : 1);
      if (Math.abs(b - a) / base < step) continue;
      moves.push({ key, before: a, after: b, better: (b > a) === higher });
    }
    const up = moves.filter((m) => m.better).length;
    const down = moves.length - up;
    if (Math.max(up, down) < 2 || up === down) continue;
    points.push({ ...g, before, after, moves, effect: up > down ? 'helped' : 'hurt', strength: Math.abs(up - down), afterTo });
  }
  return points.sort((a, b) => b.strength - a.strength || b.date.localeCompare(a.date));
}

// The changes that can't be judged yet: fewer than TURNING_MIN_AFTER days or ENOUGH_TRADES trades
// after them (with enough trades before to compare with). Newest first, with how many trades
// there are after so far and the earliest day it can be judged.
export function pendingTurningPoints(input, today = dayKey(new Date())) {
  const { trades = [] } = input;
  const days = trades.map((t) => tradingDayKey(t.date));
  const groups = [];
  for (const c of journeyChanges(input, today)) {
    const g = groups[groups.length - 1];
    if (g && c.date <= addDays(g.date, 7)) g.changes.push(c);
    else groups.push({ date: c.date, changes: [c] });
  }
  return groups.map((g) => {
    const before = days.filter((d) => d < g.date && d >= addDays(g.date, -TURNING_WINDOW)).length;
    const after = days.filter((d) => d >= g.date && d <= addDays(g.date, TURNING_WINDOW)).length;
    const readyOn = addDays(g.date, TURNING_MIN_AFTER);
    return { ...g, before, after, readyOn, waiting: before >= ENOUGH_TRADES && (readyOn > today || after < ENOUGH_TRADES) && addDays(g.date, TURNING_WINDOW) >= today };
  }).filter((g) => g.waiting).reverse();
}

// --- My edge & milestones: what works over a whole career ----------------------------------------

// A setup needs this many trades before the Journey calls it an edge or a leak. Insights judges
// sooner (edge.js SOLID); the Journey is about the long run.
export const CAREER_TRADES = 30;

// Every setup over the whole career, with each year's result: 'edge' when it earns per trade and
// made money in at least half the years it was traded, 'leak' when it loses per trade, else
// 'unproven' (too few trades, or good overall but not year after year). Plus the Behavior
// patterns that cost money, costliest first.
export function careerEdge(trades, { library = [], plays = [], accounts = [], notes = {} } = {}) {
  const all = findEdge(trades, { library, plays });
  const years = [...new Set(trades.map((t) => tradingDayKey(t.date).slice(0, 4)))].sort();
  const byYear = years.map((y) => [y, findEdge(trades.filter((t) => tradingDayKey(t.date).startsWith(y)), { library, plays })]);
  const setups = all.setups.map((st) => {
    const yearly = byYear.map(([year, r]) => {
      const x = r.setups.find((z) => z.id === st.id);
      return { year, count: x ? x.count : 0, net: x ? x.net : 0 };
    }).filter((y) => y.count > 0);
    const green = yearly.filter((y) => y.net > 0).length;
    const verdict = st.count < CAREER_TRADES ? 'unproven' : st.expectancy < 0 ? 'leak'
      : st.expectancy > 0 && green * 2 >= yearly.length ? 'edge' : 'unproven';
    return { ...st, yearly, greenYears: green, verdict };
  });
  const behavior = trades.length ? detectBehavior(trades, { accounts, notes }) : null;
  const leaks = behavior ? behavior.patterns.filter((p) => !p.costless && p.count >= 5 && p.net < 0).sort((a, b) => a.net - b.net) : [];
  return { overall: all.overall, setups, leaks, years, untagged: all.untagged, hasSetups: all.hasSetups };
}

// The milestones that are about the journey itself (passes, payouts, accounts that lasted,
// going live, a clean month), not about using the app. Built-in and the trader's own.
export const JOURNEY_MILESTONE_TYPES = ['passes', 'payout_count', 'payouts', 'account_days', 'live', 'clean_month'];

// --- Overview: the whole journey on one page -------------------------------------------------

const monthsBetween = (a, b) => {
  const [ya, ma] = a.split('-').map(Number);
  const [yb, mb] = b.split('-').map(Number);
  return (yb - ya) * 12 + (mb - ma);
};
const nextMonth = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`;
};
export const END_CAUSES = ['daily_loss', 'trailing_drawdown', 'max_loss', 'rule_break', 'consistency', 'inactivity', 'payout_done', 'went_live', 'closed', 'other'];
export const END_CAUSE_NAMES = {
  daily_loss: 'Daily loss limit', trailing_drawdown: 'Trailing drawdown', max_loss: 'Max loss', rule_break: 'Rule break', consistency: 'Consistency rule',
  inactivity: 'Inactivity', payout_done: 'Closed after payouts', went_live: 'Moved to live', closed: 'Closed', other: 'Other',
};
// The causes that mean an account was blown.
export const BLOWN_CAUSES = END_CAUSES.filter((c) => !['payout_done', 'went_live', 'closed'].includes(c));

// Every dollar in and out since the start (payouts minus everything spent), how far the journey
// is from paying for itself, month by month, and what happened to every prop account.
export function journeyOverview(accounts, trades, expenses = [], today = dayKey(new Date())) {
  const money = moneySummary(expenses, accounts, {}, today);
  const history = accountHistory(accounts, trades, expenses, today);
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const starts = [...trades.map((t) => tradingDayKey(t.date)), ...money.payments.map((e) => e.date), ...history.map((h) => h.start)].sort();
  if (!starts.length) return null;
  const firstDay = starts[0];

  // Month by month from the first one: money out and in (running totals) and the trading result.
  const pnlByMonth = {};
  const realByMonth = {};
  const ownByMonth = {};
  const propMonths = new Set();
  const simulated = new Set(accounts.filter(isSimulated).map((a) => String(a._id)));
  const own = new Set(accounts.filter((a) => !isProp(a)).map((a) => String(a._id)));
  for (const t of trades) {
    const m = tradingDayKey(t.date).slice(0, 7);
    pnlByMonth[m] = (pnlByMonth[m] || 0) + netPnl(t);
    if (!simulated.has(String(t.accountId))) realByMonth[m] = (realByMonth[m] || 0) + netPnl(t);
    else propMonths.add(m);
    if (own.has(String(t.accountId))) ownByMonth[m] = (ownByMonth[m] || 0) + netPnl(t);
  }
  const spentBy = {};
  for (const e of money.payments) spentBy[e.date.slice(0, 7)] = (spentBy[e.date.slice(0, 7)] || 0) + e.amount;
  const paidBy = {};
  for (const p of money.payouts) paidBy[p.date.slice(0, 7)] = (paidBy[p.date.slice(0, 7)] || 0) + p.amount;
  const months = [];
  let spent = 0;
  let paid = 0;
  let ownTotal = 0;
  for (let m = firstDay.slice(0, 7); m <= today.slice(0, 7) && months.length < 240; m = nextMonth(m)) {
    spent += spentBy[m] || 0;
    paid += paidBy[m] || 0;
    const ownPnl = ownByMonth[m] || 0;
    ownTotal += ownPnl;
    months.push({
      month: m, spent: spentBy[m] || 0, paid: paidBy[m] || 0, spentTotal: spent, paidTotal: paid, pnl: pnlByMonth[m] || 0, real: realByMonth[m] || 0, prop: (pnlByMonth[m] || 0) - (realByMonth[m] || 0), hasReal: m in realByMonth, hasProp: propMonths.has(m), traded: m in pnlByMonth,
      own: ownPnl, ownTotal, cash: (paidBy[m] || 0) + ownPnl - (spentBy[m] || 0), moved: Boolean(spentBy[m] || paidBy[m] || m in ownByMonth),
    });
  }
  const low = months.reduce((lo, m) => (m.paidTotal - m.spentTotal < lo.net ? { net: m.paidTotal - m.spentTotal, month: m.month } : lo), { net: 0, month: null });
  const breakEven = money.received > 0 ? months.find((m) => m.paidTotal > 0 && m.paidTotal >= m.spentTotal) : null;
  // Green months are real money: payouts plus personal trading, minus what was spent. Prop
  // evaluation and funded results are simulated, so they never make a month green.
  const moved = months.filter((m) => m.moved);
  const firstGreen = moved.find((m) => m.cash > 0);
  let greenStreak = 0;
  for (let i = moved.length - 1; i >= 0 && moved[i].cash > 0; i -= 1) greenStreak += 1;

  const prop = history.filter((h) => isProp(h.account));
  const blown = prop.filter((h) => h.outcome === 'failed' || h.outcome === 'blown');
  const evals = evaluationRecord(history);
  const funded = prop.filter((h) => h.type === 'Funded').length;
  const live = liveDays(prop).length;
  const firstPayout = money.payouts[0] || null;
  const causes = {};
  for (const h of blown) {
    const c = h.account.endCause || 'unknown';
    causes[c] = (causes[c] || 0) + 1;
  }
  const byFirm = money.byFirm.filter((f) => f.spent > 0).map((f) => ({ firm: f.firm || 'Other', spent: f.spent }));
  return {
    firstDay, months, money,
    net: money.received - money.spent, spent: money.spent, paid: money.received,
    breakEvenPct: money.spent ? (money.received / money.spent) * 100 : null,
    breakEvenMonth: breakEven ? breakEven.month : null, low,
    firstGreen: firstGreen ? firstGreen.month : null, greenStreak, greenMonths: moved.filter((m) => m.cash > 0).length,
    monthNumber: (m) => monthsBetween(firstDay.slice(0, 7), m) + 1,
    accounts: {
      bought: prop.length, blown: blown.length, passed: evals.passed, current: prop.filter((h) => !h.end).length,
      funded, live, passRate: evals.passRate, evaluations: evals.total, finished: evals.passed + evals.failed,
      avgBlownDays: blown.length ? blown.reduce((s, h) => s + h.days, 0) / blown.length : null,
    },
    payouts: money.payouts.length,
    // Only what the firms were paid: courses and tools aren't the price of a funded account.
    costPerFunded: funded ? sum(money.payments.filter((e) => expenseScope(e, byId) === 'prop'), (e) => e.amount) / funded : null,
    monthsToFirstPayout: firstPayout ? monthsBetween(firstDay.slice(0, 7), firstPayout.date.slice(0, 7)) : null,
    resets: money.payments.filter((e) => e.category === 'reset').length,
    causes: Object.entries(causes).map(([cause, count]) => ({ cause, count })).sort((a, b) => b.count - a.count),
    byFirm, history: prop,
  };
}

// --- How long prop accounts last ---------------------------------------------------------------

// Every prop account and past attempt, oldest first, with how many days it lasted (still going:
// days so far), so the page can show whether accounts are lasting longer.
export function accountLifespans(accounts, trades, today = dayKey(new Date())) {
  const list = accountHistory(accounts, trades, [], today).sort((a, b) => a.start.localeCompare(b.start));
  if (!list.length) return null;
  const rows = list.map((h) => ({ name: h.account.name, firm: h.firm, type: h.type, start: h.start, end: h.end, days: h.days, outcome: h.outcome }));
  const ended = rows.filter((h) => h.end);
  return {
    list: rows,
    first: ended[0] || null,
    latest: ended[ended.length - 1] || null,
    longest: rows.reduce((m, h) => (h.days > m.days ? h : m), rows[0]),
  };
}

// --- Monthly recap -----------------------------------------------------------------------------

function monthStats(trades, notes, accounts, month) {
  const list = trades.filter((t) => tradingDayKey(t.date).slice(0, 7) === month);
  const byDay = {};
  for (const t of list) byDay[tradingDayKey(t.date)] = (byDay[tradingDayKey(t.date)] || 0) + netPnl(t);
  const days = Object.entries(byDay).sort((a, b) => a[1] - b[1]);
  const wins = list.filter((t) => netPnl(t) > 0);
  const grossWin = sum(wins, netPnl);
  const grossLoss = -sum(list.filter((t) => netPnl(t) < 0), netPnl);
  const behavior = list.length ? detectBehavior(list, { accounts, notes }) : null;
  const journaled = Object.keys(byDay).filter((d) => isJournaled(notes[d])).length;
  const tags = {};
  for (const t of list) {
    for (const tag of t.tags || []) {
      tags[tag] = tags[tag] || { tag, net: 0, count: 0 };
      tags[tag].net += netPnl(t);
      tags[tag].count += 1;
    }
  }
  const tagList = Object.values(tags).filter((x) => x.count >= 2).sort((a, b) => b.net - a.net);
  return {
    month, trades: list.length, net: sum(list, netPnl), split: realVsProp(list, accounts), tradingDays: days.length,
    winRate: list.length ? (wins.length / list.length) * 100 : null,
    profitFactor: grossLoss ? grossWin / grossLoss : null,
    greenDays: days.filter(([, v]) => v > 0).length,
    best: days.length ? { date: days[days.length - 1][0], net: days[days.length - 1][1] } : null,
    worst: days.length ? { date: days[0][0], net: days[0][1] } : null,
    journaled, journaledShare: days.length ? (journaled / days.length) * 100 : null,
    flagged: behavior ? behavior.flagged.count : 0,
    bestTag: tagList.length && tagList[0].net > 0 ? tagList[0] : null,
    worstTag: tagList.length && tagList[tagList.length - 1].net < 0 ? tagList[tagList.length - 1] : null,
  };
}

const prevMonth = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`;
};

// One month at a glance: results against the month before, the best and worst days, what
// worked and what cost money, routine, money in and out, and the moments from the Timeline.
export function monthRecap(accounts, trades, notes = {}, expenses = [], goals = [], month) {
  const months = timeline(accounts, trades, notes, expenses, goals);
  const entry = months.find((m) => m.month === month);
  const now = monthStats(trades, notes, accounts, month);
  const before = monthStats(trades, notes, accounts, prevMonth(month));
  const inMonth = (d) => d && d.slice(0, 7) === month;
  const spent = sum(expandExpenses(expenses).filter((e) => inMonth(e.date)), (e) => e.amount);
  const payouts = sum(payoutsOf(accounts).filter((p) => inMonth(p.date)), (p) => p.amount);
  return {
    month, stats: now, before: before.trades ? before : null,
    events: entry ? entry.events : [],
    goalsReached: goals.filter((g) => inMonth(g.doneAt)).map((g) => g.title),
    money: { spent, payouts },
  };
}

// The months that have anything in them, newest first, for the recap's month picker.
export function recapMonths(trades, expenses = []) {
  const set = new Set(trades.map((t) => tradingDayKey(t.date).slice(0, 7)));
  for (const e of expandExpenses(expenses)) set.add(e.date.slice(0, 7));
  return [...set].sort().reverse();
}

// --- Milestones ---------------------------------------------------------------------------------

// Every milestone is a type and a target. The built-in ones show unless the trader hides them;
// suggestions and their own are added (user.journeyMilestones). `timeline` ones also show on the
// Timeline.
export const MILESTONE_TYPES = {
  trades: { group: 'trading', label: 'Trades logged', unit: 'count' },
  best_day: { group: 'trading', label: 'A day of at least', unit: '$' },
  green_months: { group: 'trading', label: 'Green months', unit: 'count' },
  green_streak: { group: 'trading', label: 'Green months in a row', unit: 'count' },
  journal: { group: 'routine', label: 'Days journaled', unit: 'count' },
  streak: { group: 'routine', label: 'Days journaled in a row', unit: 'count' },
  clean_month: { group: 'discipline', label: 'A month with no revenge trades', unit: 'none' },
  passes: { group: 'accounts', label: 'Evaluations passed', unit: 'count' },
  payout_count: { group: 'accounts', label: 'Payouts received', unit: 'count' },
  payouts: { group: 'accounts', label: 'Payouts adding up to', unit: '$' },
  account_days: { group: 'accounts', label: 'A prop account that lasts (days)', unit: 'days' },
  live: { group: 'accounts', label: 'Live accounts reached', unit: 'count' },
};

export const BUILT_IN_MILESTONES = [
  ['trades1', 'trades', 1], ['trades100', 'trades', 100, true], ['trades500', 'trades', 500, true], ['trades1000', 'trades', 1000, true],
  ['greenMonth', 'green_months', 1, true],
  ['journal1', 'journal', 1], ['journal30', 'journal', 30, true], ['journal100', 'journal', 100, true], ['streak10', 'streak', 10, true],
  ['cleanMonth', 'clean_month', 1, true],
  ['firstPass', 'passes', 1, true], ['firstPayout', 'payout_count', 1], ['payouts1000', 'payouts', 1000, true], ['payouts10000', 'payouts', 10000, true],
  ['account30', 'account_days', 30, true], ['firstLive', 'live', 1],
].map(([id, type, target, timeline]) => ({ id, type, target, timeline: Boolean(timeline) }));

// Ready-made ones a trader can add.
export const SUGGESTED_MILESTONES = [
  ['trades250', 'trades', 250], ['trades2500', 'trades', 2500], ['bestDay500', 'best_day', 500], ['bestDay1000', 'best_day', 1000], ['bestDay2500', 'best_day', 2500],
  ['greenMonths3', 'green_months', 3], ['greenMonths6', 'green_months', 6], ['greenStreak3', 'green_streak', 3], ['greenStreak6', 'green_streak', 6],
  ['journal60', 'journal', 60], ['journal250', 'journal', 250], ['streak20', 'streak', 20], ['streak50', 'streak', 50],
  ['passes3', 'passes', 3], ['passes5', 'passes', 5], ['payoutCount5', 'payout_count', 5], ['payouts5000', 'payouts', 5000], ['payouts25000', 'payouts', 25000],
  ['account60', 'account_days', 60], ['account90', 'account_days', 90],
].map(([id, type, target]) => ({ id, type, target, timeline: true }));

// The milestones this trader shows: built-in ones they didn't hide, then added and their own.
export function milestoneList(settings) {
  const s = settings || {};
  const hidden = new Set(s.hidden || []);
  const added = new Set(s.added || []);
  return [
    ...BUILT_IN_MILESTONES.filter((m) => !hidden.has(m.id)),
    ...SUGGESTED_MILESTONES.filter((m) => added.has(m.id)).map((m) => ({ ...m, added: true })),
    ...(s.custom || []).map((m) => ({ ...m, custom: true, timeline: true })),
  ];
}

// What the milestone types count, worked out once.
function milestoneFacts(accounts, trades, notes, today) {
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  const journaled = Object.entries(notes).filter(([, n]) => isJournaled(n)).map(([k, n]) => n.date || k).sort();
  // The day each length of journaling streak was first reached (weekends don't break it).
  const streakOn = [];
  let streak = 0;
  let prev = null;
  for (const d of journaled) {
    const gap = prev ? Math.round((new Date(`${d}T12:00:00Z`) - new Date(`${prev}T12:00:00Z`)) / 86400000) : 0;
    const weekday = new Date(`${d}T12:00:00Z`).getUTCDay();
    streak = prev && (gap === 1 || (gap <= 3 && weekday === 1)) ? streak + 1 : 1;
    if (!streakOn[streak]) streakOn[streak] = d;
    prev = d;
  }
  const history = accountHistory(accounts, trades);
  const passed = history.filter((h) => h.outcome === 'passed' && h.end).sort((a, b) => a.end.localeCompare(b.end));
  const payouts = payoutsOf(accounts);
  const props = history.filter((h) => isProp(h.account));
  const byDay = {};
  for (const t of sorted) byDay[tradingDayKey(t.date)] = (byDay[tradingDayKey(t.date)] || 0) + netPnl(t);
  const days = Object.keys(byDay).sort();
  const months = {};
  for (const t of sorted) (months[tradingDayKey(t.date).slice(0, 7)] = months[tradingDayKey(t.date).slice(0, 7)] || []).push(t);
  const done = Object.keys(months).filter((m) => m < today.slice(0, 7)).sort();
  const lastDay = (m) => tradingDayKey(months[m][months[m].length - 1].date);
  return { sorted, journaled, streakOn, passed, payouts, props, live: liveDays(props), byDay, days, months, done, lastDay, accounts };
}

function progressOf(m, f) {
  const n = m.target;
  const nth = (list, dateOf) => ({ have: Math.min(list.length, n), earnedOn: list.length >= n ? dateOf(list[n - 1]) : null });
  switch (m.type) {
    case 'trades': return nth(f.sorted, (t) => dayKey(t.date));
    case 'journal': return nth(f.journaled, (d) => d);
    case 'streak': return { have: Math.min(n, f.streakOn.length - 1 > 0 ? f.streakOn.length - 1 : 0), earnedOn: f.streakOn[n] || null };
    case 'passes': return nth(f.passed, (h) => h.end);
    case 'payout_count': return nth(f.payouts, (p) => p.date);
    case 'live': return nth(f.live, (d) => d.date);
    case 'payouts': {
      let total = 0;
      const hit = f.payouts.find((p) => (total += p.amount) >= n);
      return { have: Math.min(f.payouts.reduce((s2, p) => s2 + p.amount, 0), n), earnedOn: hit ? hit.date : null, money: true };
    }
    case 'account_days': {
      const lasted = f.props.filter((h) => h.days >= n).sort((a, b) => addDays(a.start, n).localeCompare(addDays(b.start, n)));
      return { have: Math.min(n, Math.max(0, ...f.props.map((h) => h.days))), earnedOn: lasted[0] ? addDays(lasted[0].start, n) : null };
    }
    case 'best_day': {
      const hit = f.days.find((d) => f.byDay[d] >= n);
      return { have: Math.min(n, Math.max(0, ...Object.values(f.byDay))), earnedOn: hit || null, money: true };
    }
    case 'green_months': return nth(f.done.filter((mo) => sum(f.months[mo], netPnl) > 0), (mo) => f.lastDay(mo));
    case 'green_streak': {
      let run = 0;
      let best = 0;
      let on = null;
      for (const mo of f.done) {
        run = sum(f.months[mo], netPnl) > 0 ? run + 1 : 0;
        best = Math.max(best, run);
        if (run === n && !on) on = f.lastDay(mo);
      }
      return { have: Math.min(best, n), earnedOn: on };
    }
    case 'clean_month': {
      const clean = f.done.find((mo) => f.months[mo].length >= 10 && detectBehavior(f.months[mo], { accounts: f.accounts }).patterns.find((p) => p.kind === 'revenge').count === 0);
      return { have: clean ? 1 : 0, earnedOn: clean ? f.lastDay(clean) : null };
    }
    default: return { have: 0, earnedOn: null };
  }
}

// Every milestone, earned (with the day) or with progress toward it.
export function milestones(accounts, trades, notes = {}, today = dayKey(new Date()), list = BUILT_IN_MILESTONES) {
  const f = milestoneFacts(accounts, trades, notes, today);
  return list.map((m) => {
    const p = progressOf(m, f);
    return { ...m, group: MILESTONE_TYPES[m.type].group, need: m.target, money: MILESTONE_TYPES[m.type].unit === '$', ...p };
  });
}

export const MILESTONE_GROUPS = [['trading', 'Trading'], ['routine', 'Routine'], ['discipline', 'Discipline'], ['accounts', 'Accounts and payouts']];
export const MILESTONE_TEXT = {
  trades1: ['🎬', 'First trade logged', 'Every journey starts with one.'],
  trades100: ['💯', '100 trades', 'Enough trades for your patterns to mean something.'],
  trades500: ['📚', '500 trades', 'A real sample. Your edge, or lack of it, shows now.'],
  trades1000: ['🏛️', '1,000 trades', 'A seasoned trader’s record.'],
  journal1: ['✍️', 'First journal day', 'You did the part most traders skip.'],
  journal30: ['🗓️', '30 days journaled', 'A month of showing up for your routine.'],
  journal100: ['🏅', '100 days journaled', 'Journaling is now part of how you trade.'],
  streak10: ['🔥', '10 days in a row', 'Ten trading days journaled without a break.'],
  cleanMonth: ['🧘', 'A month with no revenge trades', 'A calendar month with 10+ trades and none within 10 minutes of a loss.'],
  greenMonth: ['📈', 'First green month', 'A calendar month that finished in profit.'],
  firstPass: ['🎓', 'First evaluation passed', 'You did what most traders who buy evaluations never do.'],
  firstPayout: ['💵', 'First payout', 'Real money out of a prop firm.'],
  payouts1000: ['💰', '$1,000 in payouts', 'Payouts adding up to $1,000.'],
  payouts10000: ['🏆', '$10,000 in payouts', 'Payouts adding up to $10,000.'],
  account30: ['🛡️', 'An account that lasted 30 days', 'A prop account still alive a month after it started.'],
  trades250: ['📊', '250 trades', 'A quarter of the way to a thousand.'],
  trades2500: ['🗄️', '2,500 trades', 'A record most traders never build.'],
  bestDay500: ['🌤️', 'A $500 day', 'One trading day of $500 or more.'],
  bestDay1000: ['☀️', 'A $1,000 day', 'One trading day of $1,000 or more.'],
  bestDay2500: ['🚀', 'A $2,500 day', 'One trading day of $2,500 or more.'],
  greenMonths3: ['🌱', '3 green months', 'Three calendar months that finished in profit.'],
  greenMonths6: ['🌳', '6 green months', 'Six calendar months that finished in profit.'],
  greenStreak3: ['📈', '3 green months in a row', 'A quarter in profit, month after month.'],
  greenStreak6: ['🏔️', '6 green months in a row', 'Half a year in profit without a red month.'],
  journal60: ['📓', '60 days journaled', 'Two months of showing up for your routine.'],
  journal250: ['🏛️', '250 days journaled', 'About a year of trading days, written down.'],
  streak20: ['🔥', '20 days in a row', 'Twenty trading days journaled without a break.'],
  streak50: ['☄️', '50 days in a row', 'Fifty trading days journaled without a break.'],
  passes3: ['🎓', '3 evaluations passed', 'Passing is becoming a skill, not luck.'],
  passes5: ['🏅', '5 evaluations passed', 'Five evaluations passed.'],
  payoutCount5: ['💵', '5 payouts', 'Five payouts received.'],
  payouts5000: ['💰', '$5,000 in payouts', 'Payouts adding up to $5,000.'],
  payouts25000: ['🏆', '$25,000 in payouts', 'Payouts adding up to $25,000.'],
  account60: ['🛡️', 'An account that lasted 60 days', 'A prop account still alive two months after it started.'],
  account90: ['🏰', 'An account that lasted 90 days', 'A prop account still alive three months after it started.'],
  firstLive: ['🚀', 'Went live', 'Trading a live account with real money, the top of the prop firm ladder.'],
};
// A custom milestone's icon, by type.
export const MILESTONE_TYPE_ICONS = { trades: '📊', best_day: '☀️', green_months: '🌱', green_streak: '📈', journal: '📓', streak: '🔥', clean_month: '🧘', passes: '🎓', payout_count: '💵', payouts: '💰', account_days: '🛡️', live: '🚀' };
